import createInstance, {
  CSSInterpolation,
  Emotion,
} from "@emotion/css/create-instance";
import ClassNames from "classnames";
import {
  FunctionComponent,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { throttle } from "throttle-debounce";
import styles from "../stylesheets/components/iframe-widget.module.css";
import { insertScriptIntoHead } from "../utils/utils.js";
import Iframe from "./Iframe.js";

interface Props {
  scriptUrl: string;
  isActive: boolean;
  isPreview: boolean;
  style?: CSSInterpolation;
  children: ReactNode;
  onLoad?: (iframe: HTMLIFrameElement) => void;
}

const IframeWidget: FunctionComponent<Props> = ({
  scriptUrl,
  children,
  isActive,
  isPreview,
  style,
  onLoad,
}) => {
  const [emotionInstance, setEmotionInstance] = useState<Emotion | null>(null);
  const [iframeHeight, setIframeHeight] = useState(0);
  const [isIframeShown, setIsIframeShown] = useState(true);

  const outerObserverRef = useRef<ResizeObserver>(undefined);
  const innerObserverRef = useRef<ResizeObserver>(undefined);
  const [previousScriptUrl, setPreviousScriptUrl] = useState(scriptUrl);

  // Hide the iframe and load it again on provider setting change
  if (previousScriptUrl !== scriptUrl) {
    setIsIframeShown(false);
    setPreviousScriptUrl(scriptUrl);
  }

  const onIframeLoaded = async (iframe: HTMLIFrameElement) => {
    const iframeDocument = iframe.contentDocument;
    if (!iframeDocument) return;
    setEmotionInstance(
      createInstance({ key: `iframe-widget`, container: iframeDocument.head }),
    );

    const onResize = throttle(250, () => {
      setIframeHeight(iframeDocument.body.scrollHeight);
    });

    outerObserverRef.current = new ResizeObserver(onResize);
    outerObserverRef.current.observe(iframe);

    innerObserverRef.current = new ResizeObserver(onResize);
    innerObserverRef.current.observe(iframeDocument.body);

    await insertScriptIntoHead(scriptUrl, iframeDocument);
    onLoad?.(iframe);
  };

  useEffect(() => {
    return () => {
      outerObserverRef.current?.disconnect();
      innerObserverRef.current?.disconnect();
    };
  }, []);

  // Show the iframe again as soon as it’s hidden
  useEffect(() => {
    !isIframeShown && setIsIframeShown(true);
  }, [isIframeShown]);

  return (
    isIframeShown && (
      <Iframe
        className={ClassNames(styles.IframeWidget, {
          [styles["IframeWidget--no-click"]]: !isActive && isPreview,
        })}
        style={{ height: iframeHeight }}
        onIframeLoaded={onIframeLoaded}
        scrolling="no"
        loading="lazy"
      >
        {<div className={emotionInstance?.css(style)}>{children}</div>}
      </Iframe>
    )
  );
};

export default IframeWidget;
